import React, { useState, useEffect } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import MapPicker from "./MapPicker";
import {
  MapPin,
  Camera,
  QrCode,
  Upload,
  Search,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  Mail,
  Phone,
  User,
  Loader,
  AlertCircle,
  X,
  Star,
  Heart,
} from "lucide-react";

const FoundPetModal = ({ isOpen, onClose, user }) => {
  const { themeClasses } = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Step data
  const [stepData, setStepData] = useState({
    location: null,
    identificationMethod: null, // "photo" or "qr"
    uploadedImage: null,
    qrCodeData: null,
    matches: [],
    selectedMatch: null,
    contactForm: {
      message: "",
      name: "",
      email: "",
      phone: "",
    },
  });

  const steps = [
    {
      number: 1,
      title: "Location",
      description: "Where did you find the pet?",
    },
    {
      number: 2,
      title: "Identify",
      description: "How would you like to identify?",
    },
    { number: 3, title: "Match", description: "Finding potential matches..." },
    { number: 4, title: "Results", description: "Select the matching pet" },
    { number: 5, title: "Contact", description: "Contact the owner" },
    { number: 6, title: "Complete", description: "Notification sent!" },
  ];

  // Reset modal when opened
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(1);
      setStepData({
        location: null,
        identificationMethod: null,
        uploadedImage: null,
        qrCodeData: null,
        matches: [],
        selectedMatch: null,
        contactForm: {
          message: "",
          name: user?.displayName || "",
          email: user?.email || "",
          phone: "",
        },
      });
      setError("");
    }
  }, [isOpen, user]);

  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen, onClose]);

  // Step 1: Get User Location
  const handleLocationSelect = (locationData) => {
    setStepData((prev) => ({
      ...prev,
      location: locationData,
    }));
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser");
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          source: "gps",
          timestamp: new Date().toISOString(),
        };
        handleLocationSelect(locationData);
        setLoading(false);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setError(
          "Could not get your current location. Please select manually."
        );
        setLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Step 2: Choose Identification Method
  const handleIdentificationMethodSelect = (method) => {
    setStepData((prev) => ({
      ...prev,
      identificationMethod: method,
    }));
  };

  // Handle image upload
  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
      "image/gif",
      "image/bmp",
      "image/tiff",
      "image/tif",
    ];
    console.log("File type detected:", file.type);
    console.log("File name:", file.name);

    if (!validTypes.includes(file.type)) {
      let errorMessage = `File type "${file.type}" is not supported.`;

      // Provide specific guidance for common unsupported formats
      if (
        file.type === "image/x-adobe-dng" ||
        file.name?.toLowerCase().endsWith(".dng")
      ) {
        errorMessage =
          "DNG (Digital Negative) files are not supported. Please convert your image to JPEG or PNG format first.";
      } else if (
        file.type?.includes("raw") ||
        file.name?.toLowerCase().match(/\.(cr2|nef|arw|orf|rw2)$/)
      ) {
        errorMessage =
          "RAW image files are not supported. Please convert your image to JPEG or PNG format first.";
      } else {
        errorMessage +=
          " Please upload a JPEG, PNG, WebP, GIF, BMP, or TIFF image.";
      }

      setError(errorMessage);
      return;
    }

    // Validate file size (max 25MB)
    const maxSize = 25 * 1024 * 1024; // 25MB
    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
    console.log(`File size: ${fileSizeMB}MB`);

    if (file.size > maxSize) {
      setError(
        `Image file is too large (${fileSizeMB}MB). Please upload an image smaller than 25MB or compress the image first.`
      );
      return;
    }

    // Convert to base64 and ensure proper format
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target.result;

      // Ensure the base64 string has proper data URL format
      if (!base64String.startsWith("data:image/")) {
        setError("Invalid image format detected");
        return;
      }

      setStepData((prev) => ({
        ...prev,
        uploadedImage: base64String,
      }));

      // Clear any previous errors
      setError("");
    };

    reader.onerror = () => {
      setError("Failed to read image file. Please try again.");
    };

    reader.readAsDataURL(file);
  };

  // Handle QR code upload
  const handleQRUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // For now, we'll treat it as text input
    // In a real app, you'd use a QR code reader library
    const reader = new FileReader();
    reader.onload = (e) => {
      setStepData((prev) => ({
        ...prev,
        qrCodeData: e.target.result,
      }));
    };
    reader.readAsDataURL(file);
  };

  // Step 3: Match the Pet
  const handlePetMatching = async () => {
    setLoading(true);
    setError("");

    try {
      if (stepData.identificationMethod === "photo" && stepData.uploadedImage) {
        console.log("Starting pet identification...");
        console.log("Image data length:", stepData.uploadedImage.length);
        console.log("Image data type:", typeof stepData.uploadedImage);
        console.log(
          "Image data starts with:",
          stepData.uploadedImage.substring(0, 50)
        );

        // Call identifyPetByImage
        const identifyPetFn = httpsCallable(functions, "identifyPet");
        const result = await identifyPetFn({
          imageBase64: stepData.uploadedImage,
          location: stepData.location,
        });

        if (result.data.success && result.data.matches) {
          // Filter by distance (100km radius)
          const filteredMatches = filterByDistance(
            result.data.matches,
            stepData.location
          );
          setStepData((prev) => ({
            ...prev,
            matches: filteredMatches,
          }));
        } else {
          setStepData((prev) => ({
            ...prev,
            matches: [],
          }));
        }
      } else if (
        stepData.identificationMethod === "qr" &&
        stepData.qrCodeData
      ) {
        // Call findPetByQRCode
        const findPetFn = httpsCallable(functions, "findPetByQRCode");
        const result = await findPetFn({
          qrCodeData: stepData.qrCodeData,
        });

        if (result.data.success && result.data.pet) {
          setStepData((prev) => ({
            ...prev,
            matches: [result.data.pet],
          }));
        } else {
          setStepData((prev) => ({
            ...prev,
            matches: [],
          }));
        }
      }

      setCurrentStep(4); // Go to results
    } catch (error) {
      console.error("Pet matching error:", error);

      // Provide more specific error messages
      let errorMessage = "Failed to identify pet. Please try again.";
      if (error.message) {
        if (error.message.includes("unsupported image format")) {
          errorMessage =
            "The image format is not supported. Please try uploading a JPEG or PNG image.";
        } else if (error.message.includes("Input buffer")) {
          errorMessage =
            "There was an issue processing your image. Please try uploading a different image.";
        } else if (error.message.includes("Invalid base64")) {
          errorMessage =
            "The image could not be processed. Please try uploading the image again.";
        } else {
          errorMessage = `Error: ${error.message}`;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Filter matches by distance (100km radius)
  const filterByDistance = (matches, userLocation) => {
    if (!userLocation || !userLocation.latitude || !userLocation.longitude) {
      return matches;
    }

    return matches.filter((match) => {
      if (!match.lastKnownLocation) return true; // Include if no location data

      const distance = calculateDistance(
        userLocation.latitude,
        userLocation.longitude,
        match.lastKnownLocation.latitude,
        match.lastKnownLocation.longitude
      );

      return distance <= 100; // 100km radius
    });
  };

  // Calculate distance between two coordinates
  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Handle match selection
  const handleMatchSelect = (match) => {
    setStepData((prev) => ({
      ...prev,
      selectedMatch: match,
    }));
  };

  // Handle contact form submission
  const handleContactSubmit = async () => {
    setLoading(true);
    setError("");

    try {
      // Call sendOwnerNotificationEmail
      const sendNotificationFn = httpsCallable(
        functions,
        "sendOwnerNotificationEmail"
      );
      await sendNotificationFn({
        petId: stepData.selectedMatch.id,
        finderLocation: {
          ...stepData.location,
          description: "Found pet location",
        },
        finderInfo: {
          name: stepData.contactForm.name,
          email: stepData.contactForm.email,
          phone: stepData.contactForm.phone,
          message: stepData.contactForm.message,
        },
        notificationType: "both", // Send both email and app notification
      });

      setCurrentStep(6); // Go to completion step
    } catch (error) {
      console.error("Contact submission error:", error);
      setError("Failed to send notification. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Navigation functions
  const goToNextStep = () => {
    if (currentStep < steps.length) {
      if (currentStep === 2 && stepData.identificationMethod) {
        // Auto-trigger matching when moving from identification to matching
        setCurrentStep(3);
        setTimeout(() => handlePetMatching(), 500);
      } else {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Check if current step is complete
  const isStepComplete = () => {
    switch (currentStep) {
      case 1:
        return stepData.location !== null;
      case 2:
        return (
          stepData.identificationMethod !== null &&
          ((stepData.identificationMethod === "photo" &&
            stepData.uploadedImage) ||
            (stepData.identificationMethod === "qr" && stepData.qrCodeData))
        );
      case 3:
        return false; // This is automatic
      case 4:
        return stepData.selectedMatch !== null;
      case 5:
        return stepData.contactForm.name && stepData.contactForm.email;
      default:
        return false;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/10 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className={`${themeClasses.bg.card} border ${themeClasses.border.primary} rounded-3xl w-full max-w-4xl max-h-[90vh] shadow-2xl relative overflow-hidden`}
      >
        {/* Header */}
        <div className={`p-6 border-b ${themeClasses.border.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-500 rounded-2xl flex items-center justify-center">
                <Search className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  I Found a Pet
                </h2>
                <p className={`${themeClasses.text.secondary}`}>
                  Help reunite a pet with their family
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={`p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 ${themeClasses.text.secondary} hover:${themeClasses.text.primary} transition-colors`}
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex items-center justify-between mb-2">
              {steps.map((step, index) => (
                <div key={step.number} className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      currentStep > step.number
                        ? "bg-green-500 text-white"
                        : currentStep === step.number
                        ? "bg-blue-500 text-white"
                        : `${themeClasses.bg.secondary} ${themeClasses.text.secondary}`
                    }`}
                  >
                    {currentStep > step.number ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      step.number
                    )}
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`h-1 w-16 mx-2 ${
                        currentStep > step.number
                          ? "bg-green-500"
                          : `${themeClasses.bg.secondary}`
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className={`font-medium ${themeClasses.text.primary}`}>
                {steps[currentStep - 1]?.title}
              </p>
              <p className={`text-sm ${themeClasses.text.secondary}`}>
                {steps[currentStep - 1]?.description}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div
          className="p-6 overflow-y-auto"
          style={{ maxHeight: "calc(90vh - 200px)" }}
        >
          {error && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center space-x-3">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
              <span className="text-red-400 text-sm">{error}</span>
            </div>
          )}

          {/* Step 1: Get User Location */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <MapPin
                  className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-4`}
                />
                <h3
                  className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}
                >
                  Where did you find the pet?
                </h3>
                <p className={`${themeClasses.text.secondary} mb-6`}>
                  We'll use this location to find nearby lost pets within 100km
                </p>
              </div>

              <div className="flex justify-center mb-6">
                <button
                  onClick={getCurrentLocation}
                  disabled={loading}
                  className={`${themeClasses.bg.button} text-white px-6 py-3 rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center space-x-2`}
                >
                  {loading ? (
                    <Loader className="w-5 h-5 animate-spin" />
                  ) : (
                    <MapPin className="w-5 h-5" />
                  )}
                  <span>
                    {loading ? "Getting Location..." : "Use Current Location"}
                  </span>
                </button>
              </div>

              <div className="text-center mb-4">
                <span className={`${themeClasses.text.secondary}`}>
                  or select manually
                </span>
              </div>

              <MapPicker
                onLocationSelect={handleLocationSelect}
                initialLocation={stepData.location}
                disabled={loading}
              />
            </div>
          )}

          {/* Step 2: Choose Identification Method */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <Camera
                  className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-4`}
                />
                <h3
                  className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}
                >
                  How would you like to identify the pet?
                </h3>
                <p className={`${themeClasses.text.secondary} mb-6`}>
                  Choose the best method based on what you have available
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Photo Upload Option */}
                <div
                  className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                    stepData.identificationMethod === "photo"
                      ? "border-blue-500 bg-blue-500/10"
                      : `${themeClasses.border.primary} hover:border-blue-300`
                  }`}
                  onClick={() => handleIdentificationMethodSelect("photo")}
                >
                  <div className="text-center">
                    <Camera
                      className={`w-12 h-12 ${themeClasses.text.accent} mx-auto mb-4`}
                    />
                    <h4
                      className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}
                    >
                      Upload Photo
                    </h4>
                    <p className={`${themeClasses.text.secondary} mb-4`}>
                      Take or upload a photo for AI visual matching
                    </p>

                    {stepData.identificationMethod === "photo" && (
                      <div className="mt-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="photo-upload"
                        />
                        <label
                          htmlFor="photo-upload"
                          className={`${themeClasses.bg.button} text-white px-4 py-2 rounded-lg cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center space-x-2`}
                        >
                          <Upload className="w-4 h-4" />
                          <span>Choose Photo</span>
                        </label>

                        {stepData.uploadedImage && (
                          <div className="mt-4">
                            <img
                              src={stepData.uploadedImage}
                              alt="Uploaded pet"
                              className="w-32 h-32 object-cover rounded-lg mx-auto"
                            />
                            <p className="text-sm text-green-600 mt-2">
                              Photo uploaded successfully!
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* QR Code Option */}
                <div
                  className={`p-6 rounded-2xl border-2 cursor-pointer transition-all ${
                    stepData.identificationMethod === "qr"
                      ? "border-blue-500 bg-blue-500/10"
                      : `${themeClasses.border.primary} hover:border-blue-300`
                  }`}
                  onClick={() => handleIdentificationMethodSelect("qr")}
                >
                  <div className="text-center">
                    <QrCode
                      className={`w-12 h-12 ${themeClasses.text.accent} mx-auto mb-4`}
                    />
                    <h4
                      className={`text-xl font-bold ${themeClasses.text.primary} mb-2`}
                    >
                      Scan QR Code
                    </h4>
                    <p className={`${themeClasses.text.secondary} mb-4`}>
                      If the pet has a QR code tag for instant identification
                    </p>

                    {stepData.identificationMethod === "qr" && (
                      <div className="mt-4">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleQRUpload}
                          className="hidden"
                          id="qr-upload"
                        />
                        <label
                          htmlFor="qr-upload"
                          className={`${themeClasses.bg.button} text-white px-4 py-2 rounded-lg cursor-pointer hover:opacity-90 transition-opacity flex items-center justify-center space-x-2`}
                        >
                          <QrCode className="w-4 h-4" />
                          <span>Upload QR Code</span>
                        </label>

                        {stepData.qrCodeData && (
                          <div className="mt-4">
                            <p className="text-sm text-green-600">
                              QR code uploaded successfully!
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Matching in Progress */}
          {currentStep === 3 && (
            <div className="text-center py-12">
              <Loader
                className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-6 animate-spin`}
              />
              <h3
                className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}
              >
                Finding Matches...
              </h3>
              <p className={`${themeClasses.text.secondary}`}>
                {stepData.identificationMethod === "photo"
                  ? "Using AI to analyze the photo and find matching pets"
                  : "Looking up the QR code in our database"}
              </p>
            </div>
          )}

          {/* Step 4: Show Match Results */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center">
                <Search
                  className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-4`}
                />
                <h3
                  className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}
                >
                  Potential Matches Found
                </h3>
                <p className={`${themeClasses.text.secondary} mb-6`}>
                  {stepData.matches.length === 0
                    ? "No matches found. The pet might not be registered yet."
                    : `Found ${stepData.matches.length} potential match${
                        stepData.matches.length > 1 ? "es" : ""
                      }`}
                </p>
              </div>

              {stepData.matches.length === 0 ? (
                <div
                  className={`${themeClasses.bg.secondary} rounded-2xl p-8 text-center`}
                >
                  <AlertCircle
                    className={`w-12 h-12 ${themeClasses.text.secondary} mx-auto mb-4`}
                  />
                  <h4
                    className={`text-lg font-bold ${themeClasses.text.primary} mb-2`}
                  >
                    No Matches Found
                  </h4>
                  <p className={`${themeClasses.text.secondary} mb-4`}>
                    This pet might not be registered in our system yet. You can
                    still help by:
                  </p>
                  <ul
                    className={`${themeClasses.text.secondary} text-left space-y-2`}
                  >
                    <li>• Posting on local social media groups</li>
                    <li>• Contacting local animal shelters</li>
                    <li>• Checking for a microchip at a vet clinic</li>
                    <li>• Posting flyers in the area where you found them</li>
                  </ul>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {stepData.matches.map((match, index) => (
                    <div
                      key={index}
                      className={`p-4 rounded-2xl border-2 cursor-pointer transition-all ${
                        stepData.selectedMatch?.id === match.id
                          ? "border-green-500 bg-green-500/10"
                          : `${themeClasses.border.primary} hover:border-green-300`
                      }`}
                      onClick={() => handleMatchSelect(match)}
                    >
                      <div className="flex items-start space-x-4">
                        {match.avatarThumbnail || match.photos?.[0] ? (
                          <img
                            src={match.avatarThumbnail || match.photos[0]}
                            alt={match.name}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                            <span className="text-2xl">🐕</span>
                          </div>
                        )}

                        <div className="flex-1">
                          <h4
                            className={`text-lg font-bold ${themeClasses.text.primary}`}
                          >
                            {match.name}
                          </h4>
                          <p
                            className={`${themeClasses.text.secondary} text-sm`}
                          >
                            {match.breed} • {match.gender} • {match.age} years
                            old
                          </p>
                          {match.confidence && (
                            <div className="flex items-center space-x-1 mt-2">
                              <Star className="w-4 h-4 text-yellow-500" />
                              <span className="text-sm font-medium">
                                {Math.round(match.confidence * 100)}% match
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 5: Contact Owner */}
          {currentStep === 5 && stepData.selectedMatch && (
            <div className="space-y-6">
              <div className="text-center">
                <Mail
                  className={`w-16 h-16 ${themeClasses.text.accent} mx-auto mb-4`}
                />
                <h3
                  className={`text-2xl font-bold ${themeClasses.text.primary} mb-2`}
                >
                  Contact {stepData.selectedMatch.name}'s Owner
                </h3>
                <p className={`${themeClasses.text.secondary} mb-6`}>
                  We'll send them a notification with your contact details
                </p>
              </div>

              {/* Selected Pet Summary */}
              <div className={`${themeClasses.bg.secondary} rounded-2xl p-6`}>
                <div className="flex items-center space-x-4">
                  {stepData.selectedMatch.avatarThumbnail ||
                  stepData.selectedMatch.photos?.[0] ? (
                    <img
                      src={
                        stepData.selectedMatch.avatarThumbnail ||
                        stepData.selectedMatch.photos[0]
                      }
                      alt={stepData.selectedMatch.name}
                      className="w-20 h-20 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-20 h-20 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
                      <span className="text-3xl">🐕</span>
                    </div>
                  )}

                  <div>
                    <h4
                      className={`text-xl font-bold ${themeClasses.text.primary}`}
                    >
                      {stepData.selectedMatch.name}
                    </h4>
                    <p className={`${themeClasses.text.secondary}`}>
                      {stepData.selectedMatch.breed} •{" "}
                      {stepData.selectedMatch.gender} •{" "}
                      {stepData.selectedMatch.age} years old
                    </p>
                    {stepData.selectedMatch.owner && (
                      <p
                        className={`${themeClasses.text.secondary} text-sm mt-1`}
                      >
                        Owner: {stepData.selectedMatch.owner.name}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label
                      className={`block ${themeClasses.text.primary} font-medium mb-2`}
                    >
                      Your Name *
                    </label>
                    <input
                      type="text"
                      value={stepData.contactForm.name}
                      onChange={(e) =>
                        setStepData((prev) => ({
                          ...prev,
                          contactForm: {
                            ...prev.contactForm,
                            name: e.target.value,
                          },
                        }))
                      }
                      className={`w-full px-4 py-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} focus:border-blue-500 focus:outline-none transition-colors`}
                      placeholder="Enter your name"
                      required
                    />
                  </div>

                  <div>
                    <label
                      className={`block ${themeClasses.text.primary} font-medium mb-2`}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      value={stepData.contactForm.email}
                      onChange={(e) =>
                        setStepData((prev) => ({
                          ...prev,
                          contactForm: {
                            ...prev.contactForm,
                            email: e.target.value,
                          },
                        }))
                      }
                      className={`w-full px-4 py-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} focus:border-blue-500 focus:outline-none transition-colors`}
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    className={`block ${themeClasses.text.primary} font-medium mb-2`}
                  >
                    Phone Number (Optional)
                  </label>
                  <input
                    type="tel"
                    value={stepData.contactForm.phone}
                    onChange={(e) =>
                      setStepData((prev) => ({
                        ...prev,
                        contactForm: {
                          ...prev.contactForm,
                          phone: e.target.value,
                        },
                      }))
                    }
                    className={`w-full px-4 py-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} focus:border-blue-500 focus:outline-none transition-colors`}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label
                    className={`block ${themeClasses.text.primary} font-medium mb-2`}
                  >
                    Message (Optional)
                  </label>
                  <textarea
                    value={stepData.contactForm.message}
                    onChange={(e) =>
                      setStepData((prev) => ({
                        ...prev,
                        contactForm: {
                          ...prev.contactForm,
                          message: e.target.value,
                        },
                      }))
                    }
                    className={`w-full px-4 py-3 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-xl ${themeClasses.text.primary} focus:border-blue-500 focus:outline-none transition-colors h-24 resize-none`}
                    placeholder="Add any additional details about where/when you found the pet..."
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Completion */}
          {currentStep === 6 && (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <h3
                className={`text-2xl font-bold ${themeClasses.text.primary} mb-4`}
              >
                Notification Sent Successfully!
              </h3>
              <p
                className={`${themeClasses.text.secondary} mb-6 max-w-md mx-auto`}
              >
                We've notified the pet owner with your contact information. They
                should reach out to you soon to arrange pickup.
              </p>

              <div
                className={`${themeClasses.bg.secondary} rounded-2xl p-6 max-w-md mx-auto mb-6`}
              >
                <Heart className={`w-8 h-8 text-red-500 mx-auto mb-3`} />
                <p className={`${themeClasses.text.primary} font-medium`}>
                  Thank you for helping reunite a pet with their family! Your
                  kindness makes a real difference. 🐕❤️
                </p>
              </div>

              <div className="space-y-3">
                <button
                  onClick={() => {
                    onClose();
                    // Optionally navigate to dashboard
                  }}
                  className={`${themeClasses.bg.button} text-white px-6 py-3 rounded-xl font-medium hover:opacity-90 transition-opacity`}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer Navigation */}
        {currentStep < 6 && (
          <div
            className={`p-6 border-t ${themeClasses.border.primary} flex justify-between items-center`}
          >
            <button
              onClick={goToPreviousStep}
              disabled={currentStep === 1}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg ${
                currentStep === 1
                  ? `${themeClasses.text.tertiary} cursor-not-allowed`
                  : `${themeClasses.text.secondary} hover:${themeClasses.text.primary} hover:${themeClasses.bg.hover}`
              } transition-colors`}
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </button>

            <div className="flex items-center space-x-2">
              <span className={`${themeClasses.text.secondary} text-sm`}>
                Step {currentStep} of {steps.length}
              </span>
            </div>

            {currentStep === 3 ? (
              <div className="flex items-center space-x-2 text-blue-500">
                <Loader className="w-4 h-4 animate-spin" />
                <span>Processing...</span>
              </div>
            ) : currentStep === 5 ? (
              <button
                onClick={handleContactSubmit}
                disabled={!isStepComplete() || loading}
                className={`flex items-center space-x-2 px-6 py-2 rounded-lg font-medium transition-all ${
                  isStepComplete() && !loading
                    ? `${themeClasses.bg.button} text-white hover:opacity-90`
                    : `${themeClasses.bg.secondary} ${themeClasses.text.tertiary} cursor-not-allowed`
                }`}
              >
                {loading ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                <span>{loading ? "Sending..." : "Send Notification"}</span>
              </button>
            ) : (
              <button
                onClick={goToNextStep}
                disabled={!isStepComplete()}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all ${
                  isStepComplete()
                    ? `${themeClasses.bg.button} text-white hover:opacity-90`
                    : `${themeClasses.bg.secondary} ${themeClasses.text.tertiary} cursor-not-allowed`
                }`}
              >
                <span>Next</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FoundPetModal;
